import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { SERVICES, SAAS } from "@/data/services";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [svcOpen, setSvcOpen] = useState(false);
    const [svcMobileOpen, setSvcMobileOpen] = useState(false);
    const svcRef = useRef(null);
    const svcBtnRef = useRef(null);
    const closeTimer = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close menus on navigation
    useEffect(() => {
        setSvcOpen(false);
        setOpen(false);
        setSvcMobileOpen(false);
    }, [location.pathname, location.hash]);

    // Close desktop dropdown when clicking outside
    useEffect(() => {
        if (!svcOpen) return;
        const onDown = (e) => {
            if (svcRef.current && !svcRef.current.contains(e.target)) setSvcOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [svcOpen]);

    const openSvc = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setSvcOpen(true);
    };
    const scheduleCloseSvc = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setSvcOpen(false), 180);
    };

    const onSvcButtonKey = (e) => {
        if (e.key === "ArrowDown" && !svcOpen) {
            e.preventDefault();
            setSvcOpen(true);
            requestAnimationFrame(() =>
                svcRef.current?.querySelector("a")?.focus()
            );
        } else if (e.key === "Escape") {
            setSvcOpen(false);
        }
    };

    const onPanelKey = (e) => {
        if (e.key === "Escape") {
            e.stopPropagation();
            setSvcOpen(false);
            svcBtnRef.current?.focus();
        }
    };

    const linkCls = ({ isActive }) =>
        `text-sm tracking-wide transition-colors ${
            isActive ? "text-brand-navy" : "text-brand-gray hover:text-brand-navy"
        }`;

    const servicesActive = location.pathname.startsWith("/services");

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-brand-line"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-x flex items-center justify-between h-20">
                <Link
                    to="/"
                    data-testid="brand-logo"
                    className="flex items-center gap-3.5 group shrink-0 mr-6 lg:mr-14"
                >
                    <img
                        src="/brand/scalexa-mark-v2.png"
                        alt="Scalexa Group — consultoría empresarial, IA y aceleración comercial"
                        className="h-10 w-10 sm:h-11 sm:w-11 object-contain transition-transform duration-500 group-hover:rotate-[8deg]"
                    />
                    <span className="font-heading text-[22px] font-medium tracking-tight text-brand-navy">
                        Scalexa
                    </span>
                    <span className="hidden sm:inline label-mono ml-3 pl-3 border-l border-brand-line">Group</span>
                </Link>

                <nav className="hidden md:flex items-center gap-8 lg:gap-10">
                    <NavLink to="/" end data-testid="nav-link-inicio" className={linkCls}>
                        Inicio
                    </NavLink>

                    {/* Servicios dropdown */}
                    <div
                        ref={svcRef}
                        className="relative"
                        onMouseEnter={openSvc}
                        onMouseLeave={scheduleCloseSvc}
                    >
                        <button
                            ref={svcBtnRef}
                            type="button"
                            data-testid="nav-services-toggle"
                            aria-expanded={svcOpen}
                            aria-haspopup="true"
                            aria-controls="nav-services-panel"
                            onClick={() => openSvc()}
                            onKeyDown={onSvcButtonKey}
                            className={`flex items-center gap-1.5 text-sm tracking-wide transition-colors outline-none focus-visible:text-brand-navy ${
                                svcOpen || servicesActive
                                    ? "text-brand-navy"
                                    : "text-brand-gray hover:text-brand-navy"
                            }`}
                        >
                            Servicios
                            <ChevronDown
                                size={14}
                                className={`transition-transform duration-300 ${svcOpen ? "rotate-180" : ""}`}
                            />
                        </button>

                        {svcOpen && (
                            <div className="absolute left-0 top-full z-50 pt-3">
                                <div
                                    id="nav-services-panel"
                                    data-testid="nav-services-panel"
                                    onKeyDown={onPanelKey}
                                    className="w-80 bg-white border border-brand-line shadow-[0_28px_60px_-24px_rgba(8,37,109,0.35)]"
                                >
                                    <ul className="py-2">
                                        {SERVICES.map((s) => (
                                            <li key={s.slug}>
                                                <Link
                                                    to={`/services/${s.slug}`}
                                                    data-testid={`nav-service-${s.slug}`}
                                                    className="flex items-baseline gap-3 px-5 py-3 text-sm text-brand-navy transition-colors hover:bg-brand-soft focus-visible:bg-brand-soft focus-visible:outline-none"
                                                >
                                                    <span className="label-mono !text-[10px] shrink-0">
                                                        {s.n}
                                                    </span>
                                                    <span>{s.name}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="border-t border-brand-line">
                                        <Link
                                            to="/services"
                                            data-testid="nav-services-all"
                                            className="block px-5 py-3.5 text-sm font-medium text-brand-electric transition-colors hover:bg-brand-soft hover:text-brand-navy focus-visible:bg-brand-soft focus-visible:outline-none"
                                        >
                                            Ver todos los servicios →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <Link to="/#casos" data-testid="nav-link-casos" className="text-sm tracking-wide text-brand-gray hover:text-brand-navy transition-colors">
                        Casos y experiencia
                    </Link>

                    <NavLink to="/saas" data-testid="nav-link-saas" className={linkCls}>
                        <span className="inline-flex items-center gap-2">
                            SaaS
                            <span className="label-mono !text-[9px] border border-brand-line px-1.5 py-0.5 !text-brand-gray">
                                {SAAS.badge}
                            </span>
                        </span>
                    </NavLink>

                    <NavLink to="/about" data-testid="nav-link-nosotros" className={linkCls}>
                        Nosotros
                    </NavLink>
                    <NavLink to="/contact" data-testid="nav-link-contacto" className={linkCls}>
                        Contacto
                    </NavLink>
                </nav>

                <div className="hidden md:flex items-center gap-4">
                    <Link
                        to="/contact"
                        data-testid="nav-cta-assessment"
                        className="btn-primary text-sm py-3 px-5"
                    >
                        Agenda una reunión
                    </Link>
                </div>

                <button
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden p-2 text-brand-navy"
                    aria-label="Toggle menu"
                    aria-expanded={open}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-brand-line bg-white">
                    <div className="container-x py-6 flex flex-col gap-5">
                        <NavLink
                            to="/"
                            end
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-inicio"
                            className="text-brand-navy text-lg font-heading"
                        >
                            Inicio
                        </NavLink>

                        {/* Servicios accordion */}
                        <div>
                            <button
                                type="button"
                                data-testid="nav-mobile-services-toggle"
                                aria-expanded={svcMobileOpen}
                                aria-controls="nav-mobile-services-panel"
                                onClick={() => setSvcMobileOpen((v) => !v)}
                                className="w-full flex items-center justify-between text-brand-navy text-lg font-heading"
                            >
                                Servicios
                                <ChevronDown
                                    size={18}
                                    className={`transition-transform duration-300 ${svcMobileOpen ? "rotate-180" : ""}`}
                                />
                            </button>
                            {svcMobileOpen && (
                                <ul
                                    id="nav-mobile-services-panel"
                                    className="mt-4 ml-3 pl-4 border-l border-brand-line space-y-3"
                                >
                                    {SERVICES.map((s) => (
                                        <li key={s.slug}>
                                            <Link
                                                to={`/services/${s.slug}`}
                                                onClick={() => setOpen(false)}
                                                data-testid={`nav-mobile-service-${s.slug}`}
                                                className="block text-brand-gray hover:text-brand-navy transition-colors"
                                            >
                                                {s.name}
                                            </Link>
                                        </li>
                                    ))}
                                    <li>
                                        <Link
                                            to="/services"
                                            onClick={() => setOpen(false)}
                                            data-testid="nav-mobile-services-all"
                                            className="block font-medium text-brand-electric"
                                        >
                                            Ver todos los servicios →
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </div>

                        <Link
                            to="/#casos"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-casos"
                            className="text-brand-navy text-lg font-heading"
                        >
                            Casos y experiencia
                        </Link>
                        <NavLink
                            to="/saas"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-saas"
                            className="text-brand-navy text-lg font-heading inline-flex items-center gap-3"
                        >
                            SaaS
                            <span className="label-mono !text-[9px] border border-brand-line px-1.5 py-0.5 !text-brand-gray">
                                {SAAS.badge}
                            </span>
                        </NavLink>
                        <NavLink
                            to="/about"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-nosotros"
                            className="text-brand-navy text-lg font-heading"
                        >
                            Nosotros
                        </NavLink>
                        <NavLink
                            to="/contact"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-contacto"
                            className="text-brand-navy text-lg font-heading"
                        >
                            Contacto
                        </NavLink>
                        <Link
                            to="/contact"
                            onClick={() => setOpen(false)}
                            className="btn-primary mt-2"
                            data-testid="nav-mobile-cta"
                        >
                            Agenda una reunión
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
